import { Component, Input, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import {
  TIMER_STATES,
  EXTENSION_TIMER_CONFIGS,
  TIME_FORMATS,
  TIMER_DURATION_UNITS,
  LOW_TIMER_THRESHOLD_UNITS,
  APPEARANCE_TYPES,
} from '@pdqdev/pdq-shared/dist/utils/extensions-timer-utils';
import { BANNER_VARIANT_OPTIONS } from '@pages/checkout-app/checkout-layout/main/components/extensions/constants';
import { TimerConfiguration, TimerPreviewMode } from '@pdqdev/pdq-shared/dist/interfaces';

const COUNTDOWN_TOKEN = {
  name: '{countdown}',
  searchTerm: 'countdown',
  description: 'Display the countdown timer',
  displayName: 'Countdown Timer',
  category: 'Timer',
  replaceableName: null,
};

@Component({
  selector: 'custom-upsell-form-timer',
  templateUrl: './custom-upsell-form-timer.component.html',
  styleUrl: './custom-upsell-form-timer.component.scss',
  standalone: false,
})
export class CustomUpsellFormTimerComponent implements OnInit, OnDestroy {
  @Input() timerConfiguration: TimerConfiguration;
  @Output() timerConfigurationChange = new EventEmitter<void>();

  timerPreviewMode: TimerPreviewMode = TIMER_STATES.DEFAULT;
  timerDynamicFieldsOptions$ = new BehaviorSubject<any[]>([COUNTDOWN_TOKEN]);
  timerForm = new FormGroup({
    enableCountdownTimer: new FormControl<boolean>(false),
    timerDuration: new FormControl<number>(5),
    timerUnit: new FormControl<string>(TIMER_DURATION_UNITS.MINUTES),
    timeFormat: new FormControl<string>(TIME_FORMATS.DIGITAL),
    placement: new FormControl<string>(EXTENSION_TIMER_CONFIGS.DIGITAL_UPSELL.TIMER_PLACEMENTS.IN_ITEM_DESCRIPTION),
    defaultMessage: new FormControl<string>('Only {countdown} left to claim this offer!'),
    lowTimerMessage: new FormControl<string>('Hurry - only {countdown} left!'),
    lowTimerThreshold: new FormControl<number>(60),
    lowTimerThresholdUnit: new FormControl<string>(LOW_TIMER_THRESHOLD_UNITS.SECONDS),
    lowTimerBackgroundColor: new FormControl<string>(APPEARANCE_TYPES.WARNING),
    backgroundColor: new FormControl<string>(APPEARANCE_TYPES.INFO),
    expiredMessage: new FormControl<string>('offer expired'),
    expiredBackgroundColor: new FormControl<string>(APPEARANCE_TYPES.CRITICAL),
    expirationBehavior: new FormControl<string>(
      EXTENSION_TIMER_CONFIGS.DIGITAL_UPSELL.EXPIRATION_BEHAVIORS.SHOW_EXPIRED_MESSAGE,
    ),
  });
  timerUnitOptions = [
    {
      label: 'Minutes',
      value: TIMER_DURATION_UNITS.MINUTES,
    },
    {
      label: 'Hours',
      value: TIMER_DURATION_UNITS.HOURS,
    },
  ];
  lowTimerThresholdUnitOptions = [
    {
      label: 'Seconds',
      value: LOW_TIMER_THRESHOLD_UNITS.SECONDS,
    },
    {
      label: 'Minutes',
      value: LOW_TIMER_THRESHOLD_UNITS.MINUTES,
    },
  ];

  private destroy$ = new Subject<void>();

  constructor(private extensionsService: ExtensionsService) {}

  ngOnInit() {
    this.extensionsService.initDynamicFieldsAutocomplete();
    this.extensionsService.dynamicFieldsAutocomplete$.pipe(takeUntil(this.destroy$)).subscribe((dynamicFields) => {
      if (dynamicFields) {
        this.timerDynamicFieldsOptions$.next([COUNTDOWN_TOKEN, ...dynamicFields]);
      }
    });

    if (this.timerConfiguration) {
      this.timerForm.patchValue({
        enableCountdownTimer: this.timerConfiguration.enableCountdownTimer ?? false,
        timerDuration: this.timerConfiguration.timerDuration ?? 5,
        timerUnit: this.timerConfiguration.timerUnit ?? TIMER_DURATION_UNITS.MINUTES,
        timeFormat: this.timerConfiguration.timeFormat ?? TIME_FORMATS.DIGITAL,
        placement:
          this.timerConfiguration.placement ??
          EXTENSION_TIMER_CONFIGS.DIGITAL_UPSELL.TIMER_PLACEMENTS.IN_ITEM_DESCRIPTION,
        defaultMessage: this.timerConfiguration.defaultMessage ?? 'Only {countdown} left to claim this offer!',
        lowTimerMessage: this.timerConfiguration.lowTimerMessage ?? 'Hurry - only {countdown} left!',
        lowTimerThreshold: this.timerConfiguration.lowTimerThreshold ?? 60,
        lowTimerThresholdUnit: this.timerConfiguration.lowTimerThresholdUnit ?? LOW_TIMER_THRESHOLD_UNITS.SECONDS,
        lowTimerBackgroundColor: this.timerConfiguration.lowTimerBackgroundColor ?? APPEARANCE_TYPES.WARNING,
        backgroundColor: this.timerConfiguration.backgroundColor ?? APPEARANCE_TYPES.INFO,
        expiredMessage: this.timerConfiguration.expiredMessage ?? 'offer expired',
        expiredBackgroundColor: this.timerConfiguration.expiredBackgroundColor ?? APPEARANCE_TYPES.CRITICAL,
        expirationBehavior:
          this.timerConfiguration.expirationBehavior ??
          EXTENSION_TIMER_CONFIGS.DIGITAL_UPSELL.EXPIRATION_BEHAVIORS.SHOW_EXPIRED_MESSAGE,
      });
    }

    this.timerForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.timerConfigurationChange.emit();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTimerUnitChange(event: any) {
    this.timerForm.get('timerUnit')?.setValue(event.value);
    this.timerForm.get('timerUnit')?.markAsDirty();
  }

  onLowTimerThresholdUnitChange(event: any) {
    this.timerForm.get('lowTimerThresholdUnit')?.setValue(event.value);
    this.timerForm.get('lowTimerThresholdUnit')?.markAsDirty();
  }

  getTimerConfiguration() {
    return {
      ...this.timerForm.value,
      timerPreviewMode: this.timerPreviewMode,
    };
  }

  setTimerPreviewMode(mode: TimerPreviewMode) {
    this.timerPreviewMode = mode;
    this.timerConfigurationChange.emit();
  }

  readonly bannerVariantOptions = BANNER_VARIANT_OPTIONS;
  readonly TIMER_STATES = TIMER_STATES;
  readonly TIMER_PLACEMENTS = EXTENSION_TIMER_CONFIGS.DIGITAL_UPSELL.TIMER_PLACEMENTS;
  readonly EXPIRATION_BEHAVIORS = EXTENSION_TIMER_CONFIGS.DIGITAL_UPSELL.EXPIRATION_BEHAVIORS;
  readonly TIME_FORMATS = TIME_FORMATS;
}
